import React, { useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import {
  Typography,
  Button,
  Box,
  Alert
} from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LogoutIcon from '@mui/icons-material/Logout';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import AdminModelTypes from './pages/AdminModelTypes';
import CreateModelType from './pages/CreateModelType';
import ModelTypeDetail from './pages/ModelTypeDetail';

import './App.css';
import CreateModel from './pages/CreateModel';
import { UserProvider,useUser } from './context/UserContext';
import ModelDetails from './pages/ModelDetails';
import Home from './pages/Home';
import { UserModelsProvider } from './context/UserModelsContext';
import { BACKEND_URL } from './utils/constants';
import AppRoutes from './AppRoutes';
import TermsModal from './components/TermsModal';
import { Wordmark } from './components/Wordmark';
import { ThemeProvider } from '@mui/material/styles';
import { brand, colors, secondaryOnDarkButtonSx, theme } from './theme';
import { AppLayout } from './components/AppLayout';
import { ConditionalLayout } from './components/ConditionalLayout';
import { LayoutProvider } from './context/LayoutContext';


import { ConstructionLoader } from './components/ConstructionLoader';
import { getPendingInvite } from './utils/invite';
import { isPolicyRefusal, rememberedLoginParams, signInAccepted, stepUpFor } from './utils/signInPolicy';

// Messages for the two account-status refusals requires_auth can return
// (uw_backend/app/auth/auth0.py, check_account_status).
const ACCOUNT_STATUS_MESSAGES: Record<string, string> = {
  account_disabled: 'This account has been disabled. Contact info@underwritre.com if you think this is a mistake.',
  email_not_verified: 'Please verify your email address using the link we sent you, then log in again.',
};

function App() {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently
  } = useAuth0();

  const [backendMessage, setBackendMessage] = useState<string | null>(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authErrorOpen, setAuthErrorOpen] = useState<boolean>(false);

  // On load, show any persisted auth error message (saved before logout redirect)
  useEffect(() => {
    const persisted = sessionStorage.getItem('uw_auth_error_message');
    if (persisted) {
      setAuthError(persisted);
      setAuthErrorOpen(true);
      sessionStorage.removeItem('uw_auth_error_message');
    }
  }, []);

  // Capture Auth0 redirect errors and clear local auth
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const err = params.get('error');
      const desc = params.get('error_description');
      if (err && desc) {
        const decoded = decodeURIComponent(desc);
        setAuthError(decoded);
        setAuthErrorOpen(true);
        // Trigger a one-time logout to clear Auth0 session to prevent auto-login loops
        const alreadyLoggedOut = sessionStorage.getItem('uw_auth_error_logged_out') === '1';
        if (!alreadyLoggedOut) {
          // Persist the message so we can show it after returning to a clean URL
          sessionStorage.setItem('uw_auth_error_message', decoded);
          sessionStorage.setItem('uw_auth_error_logged_out', '1');
          try {
            // Mirror NavBar logout behavior (no federated), return to clean origin
            logout({ logoutParams: { returnTo: window.location.origin } });
          } catch {}
        }
      }
    } catch {}
  }, [logout]);
  useEffect(() => {
    const fetchUser = async () => {

      if (!user?.email) {
        console.error("No email found in user object");
        return;
      }
  
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "openid profile email"
          }
        });

        const res = await fetch(BACKEND_URL + "/api/check_user", {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          mode: 'cors'
        });
        if (!res.ok) {
          let errorData: any = {};
          try { errorData = await res.json(); } catch {}
          // SEC-05: the backend now refuses disabled accounts and unverified
          // email addresses at the door. Show why and clear the Auth0 session
          // so the user does not sit on a blank app that 403s every call.
          // Enterprise batch 10: the firm requires two-factor or its SSO, and
          // this sign-in did not use it. Sign in again the right way, once;
          // a second refusal signs out with an explanation instead of looping.
          if (isPolicyRefusal(res.status, errorData)) {
            const next = stepUpFor(errorData, user?.email);
            if ('redirect' in next) {
              loginWithRedirect({ authorizationParams: next.redirect });
            } else {
              sessionStorage.setItem('uw_auth_error_message', next.message);
              try {
                logout({ logoutParams: { returnTo: window.location.origin } });
              } catch {}
            }
            return;
          }
          const blocked = ACCOUNT_STATUS_MESSAGES[errorData?.code as string];
          if (res.status === 403 && blocked) {
            sessionStorage.setItem('uw_auth_error_message', blocked);
            try {
              logout({ logoutParams: { returnTo: window.location.origin } });
            } catch {}
            return;
          }
          throw new Error(errorData.error || errorData.description || 'Failed to fetch');
        }
        const data = await res.json();
        signInAccepted();
        setBackendMessage(data.message);

        // After user is ensured, check user_info for terms acceptance
        try {
          const infoRes = await fetch(BACKEND_URL + "/api/user_info", {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            credentials: 'include',
            mode: 'cors'
          });
          if (infoRes.ok) {
            const info = await infoRes.json();
            const accepted = Boolean(info && info.accepted_terms_and_conditions);
            if (!accepted) setTermsOpen(true);
          }
        } catch {}
      } catch (err) {
        console.error("Error calling backend:", err);
        // logout(); // Log the user out if the request fails
      }
    };
  
    if (isAuthenticated) {
      fetchUser();
    }
  }, [isAuthenticated, getAccessTokenSilently, user, logout, loginWithRedirect]);


  const adminEmails = process.env.REACT_APP_ADMIN_EMAILS?.split(',') || [];

  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: brand.ink }}>
        <ConstructionLoader surface="dark" />
      </Box>
      </ThemeProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: brand.ink,
          px: { xs: 2, sm: 3 }
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: 440, md: 480 },
            mx: 'auto',
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: `${brand.radius}px`,
            textAlign: 'center',
            bgcolor: brand.ink2,
            border: `1px solid ${brand.lineDarkBg}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Wordmark fontSize={32} />
          </Box>
          <Typography
            variant="h2"
            sx={{
              mb: 1.25,
              color: colors.white,
              fontSize: { xs: '1.375rem', sm: '1.5rem' }
            }}
          >
            Log in to access UnderwritRE
          </Typography>
          <Typography variant="body1" sx={{ mb: { xs: 3, md: 4 }, color: brand.mutedDarkBg }}>
            Financial real estate underwriting
          </Typography>
          {getPendingInvite() && (
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              You have been invited to join a firm. Sign up or log in with the email address the invitation was sent to.
            </Alert>
          )}
          <Button
            variant="contained"
            size="large"
            startIcon={<LockOpenIcon />}
            onClick={() =>
              loginWithRedirect({
                authorizationParams: {
                  prompt: 'login',    // force showing the Universal Login screen
                  max_age: 0,         // ignore any existing SSO session
                  // two-factor up front if this browser's firm requires it (batch 10)
                  ...rememberedLoginParams()
                }
              })
            }
            fullWidth
          >
            Log in with Email or Google
          </Button>
          <Box sx={{ mt: 1.5 }}>
            <Button
              size="large"
              onClick={() =>
                loginWithRedirect({
                  authorizationParams: { screen_hint: 'signup' }
                })
              }
              sx={secondaryOnDarkButtonSx}
              fullWidth
            >
              Don't have an account? Sign up
            </Button>
          </Box>
        </Box>
        {authErrorOpen && authError && (
          <Box sx={{ position: 'absolute', bottom: 24, left: 0, right: 0, display: 'flex', justifyContent: 'center', px: 2 }}>
            <Alert
              severity="warning"
              onClose={() => setAuthErrorOpen(false)}
              sx={{ width: '100%', maxWidth: 960 }}
            >
              {authError}
            </Alert>
          </Box>
        )}
      </Box>
      </ThemeProvider>
    );
  }

  return (
  <ThemeProvider theme={theme}>
    <LayoutProvider>
    <UserProvider>
      <UserModelsProvider>
        <Router>
          <ConditionalLayout>
            <AppRoutes />
            {authErrorOpen && authError && (
              <Box sx={{ mt: 2, mb: 3, px: 2, display: 'flex', justifyContent: 'center' }}>
                <Alert
                  severity="warning"
                  onClose={() => setAuthErrorOpen(false)}
                  sx={{ width: '100%', maxWidth: 960 }}
                >
                  {authError}
                </Alert>
              </Box>
            )}
            <TermsModal
              open={isAuthenticated && termsOpen}
              onCancel={() => setTermsOpen(false)}
              onAccept={async () => {
                try {
                  const token = await getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE, scope: 'openid profile email' }
                  });
                  await fetch(BACKEND_URL + "/api/user_info", {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } as any,
                    credentials: 'include',
                    body: JSON.stringify({
                      accepted_terms_and_conditions: true,
                      accepted_terms_and_conditions_date: new Date().toISOString()
                    })
                  });
                } catch {}
                setTermsOpen(false);
              }}
            />
          </ConditionalLayout>
        </Router>
      </UserModelsProvider>
    </UserProvider>
    </LayoutProvider>
  </ThemeProvider>
);
}

export default App;